import { FC, ReactElement, useEffect } from 'react';

import {
    Link,
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import { Logo, Page, Wrapper } from '../../../components';
import { DefaultHelmet } from '../../../compositions';
import { ConnectedBlockRenderer, ConnectedCheckout } from '../../../connectors';
import { CheckoutNavigation } from '../../../containers';
import {
    orderParamKey,
    PaymentStatus,
    stateParamKey,
} from '../../../entities/@api/Sylius';
import { CheckoutParams, CheckoutTab } from '../../../entities/@shop/Checkout/Checkout';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { useRouteByTranslatedPath, useTrans } from '../../../hooks';
import { useTypedDispatch } from '../../../redux/store';
import { addNegativeToast } from '../../../redux/toast/toastActions';

import './CheckoutPage.scss';

interface CheckoutPageProps {
    page?: string;
}

export const CheckoutPage: FC<CheckoutPageProps> = (): ReactElement => {
    const trans = useTrans();
    const dispatch = useTypedDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const routeByTranslatedPath = useRouteByTranslatedPath();
    const { id } = useParams<CheckoutParams>();

    const translatedTab = routeByTranslatedPath(pathname, { id }) as CheckoutTab;

    const navigateToThankYouPage = (orderId: string): void => {
        const path = trans(appRoutes[AppRoute.checkoutThankYou].path);
        const params = new URLSearchParams();

        params.set(orderParamKey, orderId);

        const paramString = params.toString();
        const query = `?${paramString}`;

        navigate(path + query);
    };

    useEffect((): void => {
        const orderId = searchParams.get(orderParamKey);
        const orderState = searchParams.get(stateParamKey);

        if (!orderId) {
            return;
        }

        if (orderState === PaymentStatus.error) {
            navigate(trans(appRoutes[AppRoute.checkoutPayment].path));

            dispatch(addNegativeToast({
                title: trans('pages.checkout.paymentHasFailedTitle'),
                description: trans('pages.checkout.paymentHasFailedDescription'),
            }));

            return;
        }

        if (orderState === PaymentStatus.success || orderState === PaymentStatus.pending) {
            navigateToThankYouPage(orderId);
        }
    }, [searchParams]);

    return (
        <Page className="checkout-page">
            <DefaultHelmet
                title={trans('pages.checkout.meta.title')}
                description={trans('pages.checkout.meta.description')}
            />

            <Wrapper className="checkout-page__navigation-wrapper">
                {translatedTab !== CheckoutTab.order && (
                    <Link
                        title={trans('pages.checkout.homeLink')}
                        to={trans(appRoutes[AppRoute.home].path)}
                        className="checkout-page__logo-wrapper"
                    >
                        <Logo className="checkout-page__logo" />
                    </Link>
                )}

                <CheckoutNavigation />
            </Wrapper>

            <Wrapper className="checkout-page__contents-wrapper">
                <ConnectedCheckout tab={translatedTab} />
            </Wrapper>

            {translatedTab === CheckoutTab.order && (
                <ConnectedBlockRenderer appRoute={AppRoute.checkoutOrder} />
            )}
        </Page>
    );
};
